import { immerable } from 'immer'
import { useState, useRef } from 'react'
import api from '@/services/api/messages.js'
import titleCase from '@/utils/title-case'
import KeyHelper from '@/utils/key-helper'

class Message {

	[immerable] = true

	constructor(params) {
		this.created = params.created
		this.dataElement = params.dataElement
		this.status = params.status
		this.system = params.system
		this.on_behalf_of_id = params.on_behalf_of_id
		this.on_behalf_of_name = params.on_behalf_of_name
		this.type = params.type
		this.verificationStatus = params.verificationStatus
		this.sysId = params.sysId
		this.orgId = params.orgId
		this.referenceId = params.referenceId
		this.isShared = params.isShared
		this.isTTFlow = params?.isTTFlow
		this.consumedOrgId = params.consumedOrgId
		this.consumedSysId = params.consumedSysId
		this.receivingType = params.receivingType
		this.providerTransactionStatus = params?.providerTransactionStatus

		this.id = params.id
		this.$selected = false
	}
}

const useMessages = (defaultData = []) => {

	const [itemsData, setItems] = useState(defaultData)
	const [metaData, setMeta] = useState(null)
	const [currentItem, setCurrentItem] = useState(null)
	const [loading, setLoading] = useState(false)
	const [errorMsg, setErrorMsg] = useState()
	
	const lastRequestPayloadKeyRef = useRef(null)

	async function fetch(option) {

		setLoading(true)

		const [error, response] = await api.index(option)

		setLoading(false)

		if (error) {
			return setErrorMsg(await errorMessage(error));
		}

		const { items, meta } = response

		if (!Array.isArray(items)) {
			return setErrorMsg('Messages is not an array')
		}

		const serializedData = items.map(entry => serialize(entry))
		const newItems = serializedData.map(params => new Message(params))

		setMeta(meta)
		setItems(newItems)
	}

	async function postFetch(option) {
		
		const currentPayloadKey = KeyHelper.createKeyOfObject(option)

		if (KeyHelper.isSameKey(lastRequestPayloadKeyRef.current, currentPayloadKey)) return
		
		lastRequestPayloadKeyRef.current = currentPayloadKey

		setLoading(true)

		const [error, response] = await api.getMessages(option)

		setLoading(false)

		if (error) {
			return setErrorMsg(await errorMessage(error));
		}

		const { items, meta } = response

		if (!Array.isArray(items)) {
			return setErrorMsg('Messages is not an array')
		}

		const serializedData = items.map(entry => serialize(entry))
		const newItems = serializedData.map(params => new Message(params))

		setMeta(meta)
		setItems(newItems)
	}


	async function errorMessage(error)
	{
		return error;
	}

	async function create(payload) {
		return new Promise(async (resolve, reject) => {

			const [error, response] = await api.create(payload)

			if (error) {
				return reject(error)
			}

			return resolve(response)
		})
	}

	function serialize(entry) {
		return {
			id: entry.id,
			created: entry.created,
			system: {
				id: entry.orgId,
			},
			sysId: entry.systemId,
			orgId: entry.orgId,
			status: titleCase(entry.messageStatus),
			dataElement: {
				name: entry.dataElement.elementName,
				id: entry.dataElement.dataElementId,
			},
			on_behalf_of_id: entry.onBehalfOf && entry.onBehalfOf[0] ? entry.onBehalfOf[0].id : null,
			on_behalf_of_name: entry.onBehalfOf && entry.onBehalfOf[0] ? entry.onBehalfOf[0].name : '',
			type: entry.messageType,
			verificationStatus: entry.verificationStatus,
			referenceId: entry.referenceId,
			isShared: entry.isSharingEnabled,
			isTTFlow: entry?.isTTFlow,
			consumedOrgId: entry.consumedOrgId,
			consumedSysId: entry.consumedSysId,
			receivingType: entry.receivingType,
			providerTransactionStatus: entry?.providerTransactionStatus
		}
	}

	function view(id) {
		const newCurrentItem = itemsData.find(item => item.id === id)
		setCurrentItem(newCurrentItem)
	}

	return {
		create,
		currentItem,
		error: errorMsg,
		fetch,
		items: itemsData,
		loading,
		meta: metaData,
		setCurrentItem,
		setItems,
		view,
		postFetch,
	}

}

export default useMessages
